import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { TextareaFormField } from 'src/shared/components/ui/forms/form-fields/textarea'
import { ResponsivePopup } from 'src/shared/components/ui/popups/responsive-popup'

import { useCreateNote } from '../../api/use-create-note'
import { useEditNote } from '../../api/use-edit-note'
import type { BusinessNote } from '../../schemas/note-schema'

interface NoteModalProps {
  isOpen: boolean
  onClose: () => void
  note?: BusinessNote
  referralShareId?: number
  leadInquiryId?: number
}

export const NoteModal = ({ isOpen, onClose, note, referralShareId, leadInquiryId }: NoteModalProps) => {
  const form = useForm({
    defaultValues: { title: '', content: '' },
  })

  const { handleSubmit, reset, control } = form

  const createNote = useCreateNote()
  const updateNote = useEditNote()

  useEffect(() => {
    if (note) {
      reset(note)
    } else {
      reset({ title: '', content: '' })
    }
  }, [note, reset])

  const onSubmit = (data: { title: string; content: string }) => {
    if (note) {
      updateNote.mutate({ id: note.id, content: data.content, title: data.title })
    } else {
      createNote.mutate({ content: data.content, referralShareId, leadInquiryId })
    }
    onClose()
  }

  // Using a dummy div as trigger since we're controlling open state externally
  const dummyTrigger = <div style={{ display: 'none' }} />

  const footer = (
    <Button type="submit" variant="default" className="w-full" form="note-form">
      {note ? 'Save changes' : 'Save'}
    </Button>
  )

  return (
    <ResponsivePopup
      trigger={dummyTrigger}
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title={note ? 'Edit note' : 'Add note'}
      className="mx-auto"
      footerClassName="border-t-0"
      footer={footer}
    >
      <Form {...form}>
        <form id="note-form" onSubmit={handleSubmit(onSubmit)} className="pb-4">
          <div className="space-y-4">
            {note && <InputFormField control={control} name="title" label="Title" floatingLabel={true} />}

            <TextareaFormField
              control={control}
              name="content"
              label="Note"
              minRows={3}
              maxRows={8}
              floatingLabel={true}
              description="Add a note..."
              fieldClassName="bg-background"
              maxLength={500}
            />
          </div>
        </form>
      </Form>
    </ResponsivePopup>
  )
}
